import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Play, Clock, HelpCircle, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/pages/CollegePredictorPage";
import { TypeBadge, PatternBadge, LockedPill, SampleNote, Crumb, err, startTest, TYPE_LABEL } from "./shared";

export default function PrepTests() {
  const { examId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const type = params.get("type") || "";
  const [data, setData] = useState(null);
  const [active, setActive] = useState({});

  useEffect(() => {
    api.get(`/test-prep/tests?exam_id=${examId}`).then((r) => setData(r.data)).catch((e) => toast.error(err(e)));
    api.get(`/test-prep/attempts?exam_id=${examId}`).then((r) => {
      const m = {};
      r.data.filter((a) => a.status === "in_progress").forEach((a) => { m[a.test_id] = a.id; });
      setActive(m);
    }).catch(() => {});
  }, [examId]);

  if (!data) return <div className="p-8 text-slate-400">Loading...</div>;
  const tests = data.tests.filter((t) => !type || t.test_type === type);

  return (
    <div data-testid="prep-tests" className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Crumb items={[{ to: "/prep", label: "Test Preparation" }, { to: `/prep/exam/${examId}`, label: data.exam.name }, { label: type ? TYPE_LABEL[type] : "All tests" }]} />
      <Header icon={ListChecks} eyebrow={`${data.exam.name} ${data.exam.session}`} title={type ? `${TYPE_LABEL[type]}s` : "All tests"} desc="Pick a test. Your attempt is saved as you go — refreshing will not lose progress." />
      <div className="-mt-4 mb-4"><PatternBadge status={data.exam.pattern_status} /></div>
      <SampleNote />

      <div className="mt-5 flex flex-wrap gap-2">
        <Tab label="All" on={!type} onClick={() => setParams({})} testid="tests-tab-all" />
        {data.test_types.map((t) => <Tab key={t.id} label={t.label} on={type === t.id} onClick={() => setParams({ type: t.id })} testid={`tests-tab-${t.id}`} />)}
      </div>

      {tests.length === 0 && <p data-testid="tests-empty" className="mt-6 text-sm text-slate-500">No {type ? TYPE_LABEL[type].toLowerCase() : "test"}s are available yet for this exam — more questions are needed before this level can be offered.</p>}

      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {tests.map((t) => {
          const resume = active[t.id];
          return (
            <div key={t.id} data-testid={`test-card-${t.test_type}`} className="rounded-2xl border border-white/8 bg-[#131C2E] p-5">
              <div className="flex flex-wrap items-center gap-2"><TypeBadge type={t.test_type} />{t.locked && <LockedPill />}</div>
              <p className="mt-3 font-semibold">{t.title}</p>
              <p className="mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1"><HelpCircle className="h-3.5 w-3.5" /> {t.question_count} questions</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.minutes} min</span>
                {t.marks_correct != null && <span>+{t.marks_correct} / {t.marks_wrong}</span>}
              </p>
              {t.test_type === "mock" && !t.full_length && (
                <p className="mt-2 text-xs text-amber-300">Sample mock: {t.question_count} of {t.pattern_questions} pattern questions available. Rank estimate needs a full-length mock.</p>
              )}
              <button data-testid={`start-test-btn-${t.test_type}`} disabled={t.locked && !resume}
                onClick={() => resume ? navigate(`/prep/test/${resume}`) : startTest(navigate, t.id)}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-xs font-bold text-[#041016] transition-transform hover:scale-105 disabled:opacity-50">
                <Play className="h-3.5 w-3.5" /> {resume ? "Resume test" : t.locked ? "Premium only" : "Start test"}
              </button>
              {t.locked && !resume && user?.plan === "free" && <p className="mt-2 text-[11px] text-slate-500">Upgrade on the Pricing page to unlock premium tests.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Tab = ({ label, on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${on ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"}`}>{label}</button>
);
